import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { PackageSearch, Plus, Trash2, Camera, Loader2, X } from "lucide-react";

const emptyLine = () => ({ product: "", qty: "", unit: "τεμ", photo: "" });
const blank = () => Array.from({ length: 6 }, emptyLine);
const UNITS = ["τεμ", "κιβ", "πακ", "lt", "kg", "ρολό"];
const cls = "w-full rounded-lg border border-[#E3DED4] bg-white px-2.5 py-2 text-sm outline-none focus:border-[#45423C]";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data.file_id;
}

function LinePhoto({ i, photo, onChange }) {
  const [busy, setBusy] = useState(false);
  if (photo) {
    return (
      <div className="relative h-10 w-10 shrink-0">
        <img src={fileUrl(photo)} alt="" className="h-10 w-10 rounded-lg object-cover border border-[#E3DED4]" />
        <button type="button" data-testid={`hk-stock-photo-remove-${i}`} onClick={() => onChange("")} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-white border border-[#E3DED4] flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
      </div>
    );
  }
  return (
    <label data-testid={`hk-stock-photo-${i}`} className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-dashed border-[#D8D0C2] flex items-center justify-center text-[#6F6A60] hover:border-[#6E7A63]">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return; setBusy(true);
        try { onChange(await uploadFile(f)); } catch (err) { toast.error(apiErr(err)); }
        setBusy(false); e.target.value = "";
      }} />
    </label>
  );
}

function History({ counts, isManager, onDelete }) {
  if (!counts.length) return <p className="text-sm text-[#6F6A60]" data-testid="hk-stock-history-empty">Δεν υπάρχουν καταμετρήσεις ακόμα.</p>;
  return (
    <div className="space-y-3">
      {counts.map((c) => (
        <details key={c.id} className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid={`hk-stock-count-${c.id}`}>
          <summary className="cursor-pointer flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-[#45423C]">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
            <span className="text-[#6F6A60]">{c.counted_by_name}{c.unit_name ? ` · ${c.unit_name}` : ""} · {c.lines.length} προϊόντα</span>
            {isManager && <button data-testid={`hk-stock-delete-${c.id}`} onClick={(e) => { e.preventDefault(); if (window.confirm("Διαγραφή καταμέτρησης;")) onDelete(c.id); }} className="ml-auto text-[#991B1B]"><Trash2 className="h-4 w-4" /></button>}
          </summary>
          <table className="w-full text-sm mt-3">
            <tbody>
              {c.lines.map((l) => (
                <tr key={`${c.id}-${l.product}-${l.qty}-${l.photo}`} className="border-t border-[#E3DED4]">
                  <td className="py-1.5">{l.product}</td>
                  <td className="py-1.5 text-right tabular whitespace-nowrap">{l.qty} {l.unit}</td>
                  <td className="py-1.5 pl-3 w-12">{l.photo && <a href={fileUrl(l.photo)} target="_blank" rel="noreferrer"><img src={fileUrl(l.photo)} alt="" className="h-9 w-9 rounded object-cover" /></a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {c.notes && <p className="text-xs text-[#6F6A60] mt-2">{c.notes}</p>}
        </details>
      ))}
    </div>
  );
}

export default function HousekeepingStock({ embedded }) {
  const qc = useQueryClient();
  const [lines, setLines] = useState(blank);
  const [unitId, setUnitId] = useState("");
  const [notes, setNotes] = useState("");
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });
  const { data: units = [] } = useQuery({ queryKey: ["fo-units"], queryFn: async () => (await api.get("/fieldops/units")).data });
  const { data: counts = [] } = useQuery({ queryKey: ["fo-stock"], queryFn: async () => (await api.get("/fieldops/stock-counts")).data });
  const isManager = ["owner", "hr", "manager"].includes(me?.role);
  const known = Array.from(new Set(counts.flatMap((c) => c.lines.map((l) => l.product)))).sort();
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = useMutation({
    mutationFn: async () => (await api.post("/fieldops/stock-counts", {
      unit_id: unitId, notes, lines: lines.filter((l) => l.product.trim()).map((l) => ({ ...l, qty: Number(l.qty) || 0 })),
    })).data,
    onSuccess: (d) => { toast.success(`Η καταμέτρηση αποθηκεύτηκε (${d.lines.length} προϊόντα)`); setLines(blank()); setNotes(""); qc.invalidateQueries({ queryKey: ["fo-stock"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/fieldops/stock-counts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fo-stock"] }),
  });
  const filled = lines.some((l) => l.product.trim());
  return (
    <div className="space-y-6 max-w-3xl" data-testid="hk-stock-page">
      {!embedded && <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Housekeeping</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><PackageSearch className="h-7 w-7" /> Καταμέτρηση υλικών</h1>
      </div>}
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 md:p-5 space-y-3">
        <select data-testid="hk-stock-unit" value={unitId} onChange={(e) => setUnitId(e.target.value)} className={cls}>
          <option value="">Αποθήκη / γενικά</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <datalist id="hk-stock-products">{known.map((p) => <option key={p} value={p} />)}</datalist>
        <div className="space-y-2">
          {lines.map((l, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex items-center gap-2" data-testid={`hk-stock-line-${i}`}>
              <input data-testid={`hk-stock-product-${i}`} list="hk-stock-products" value={l.product} onChange={(e) => setLine(i, "product", e.target.value)} placeholder="Προϊόν" className={`${cls} flex-1 min-w-0`} />
              <input data-testid={`hk-stock-qty-${i}`} type="number" inputMode="decimal" min="0" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder="Ποσ." className={`${cls} !w-[70px] shrink-0 text-right`} />
              <select data-testid={`hk-stock-uom-${i}`} value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} className={`${cls} !w-[72px] shrink-0 px-1.5`}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <LinePhoto i={i} photo={l.photo} onChange={(v) => setLine(i, "photo", v)} />
            </div>
          ))}
        </div>
        <button data-testid="hk-stock-add-lines" onClick={() => setLines((ls) => [...ls, ...Array.from({ length: 3 }, emptyLine)])} className="text-sm text-[#C86235] inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Περισσότερες γραμμές</button>
        <textarea data-testid="hk-stock-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Σημειώσεις (π.χ. τι λείπει)" className={cls} />
        <button data-testid="hk-stock-save" disabled={!filled || save.isPending} onClick={() => save.mutate()} className="w-full rounded-xl bg-[#6E7A63] text-white py-3 text-sm font-semibold disabled:opacity-50">{save.isPending ? "Αποθήκευση…" : "Αποθήκευση καταμέτρησης"}</button>
      </div>
      <div className="space-y-3">
        <h2 className="font-medium text-[#45423C]">Προηγούμενες καταμετρήσεις</h2>
        <History counts={counts} isManager={isManager} onDelete={(id) => del.mutate(id)} />
      </div>
    </div>
  );
}
